// This file is auto-generated by @hey-api/openapi-ts

import { z } from 'zod';

/**
 * AuditReviewCreate
 */
export const zAuditReviewCreate = z.object({
    verdict: z.enum(['confirmed', 'both_wrong']),
    reason: z.string().nullish(),
    corrected_value: z.unknown().optional()
});

/**
 * CaseCreate
 */
export const zCaseCreate = z.object({
    source_rfp_id: z.string().regex(/^[1-9][0-9]{0,18}$/),
    label: z.string().max(1024).nullish(),
    use_final_documents: z.boolean().optional().default(true)
});

/**
 * CaseOut
 */
export const zCaseOut = z.object({
    id: z.number().int(),
    source_rfp_id: z.string(),
    source_folder: z.string().nullish(),
    use_final_documents: z.boolean(),
    state: z.enum([
        'awaiting_refresh',
        'draft',
        'in_review',
        'golden'
    ]),
    correction_revision: z.number().int(),
    qualifying_score_id: z.number().int().nullish(),
    label: z.string().nullish(),
    label_updated_by: z.string().nullish(),
    label_updated_at: z.string().datetime({ offset: true }).nullish(),
    created_by: z.string(),
    created_at: z.string().datetime({ offset: true }),
    source_rfp_url: z.string().nullable()
});

/**
 * CaseRunCreate
 */
export const zCaseRunCreate = z.object({
    replay_kind: z.string().optional().default('submission')
});

/**
 * CaseRunOut
 *
 * A run on a case, with the replay RFP RUMI filled in.
 */
export const zCaseRunOut = z.object({
    id: z.number().int(),
    suite_id: z.number().int().nullish(),
    replay_kind: z.string(),
    replay_rfp_id: z.string().nullish(),
    state: z.enum([
        'queued',
        'running',
        'finished',
        'failed'
    ]),
    error_message: z.string().nullish(),
    created_at: z.string().datetime({ offset: true }),
    finished_at: z.string().datetime({ offset: true }).nullish(),
    replay_rfp_url: z.string().nullable()
});

/**
 * CorrectionCreate
 */
export const zCorrectionCreate = z.object({
    field_path: z.string(),
    kind: z.string(),
    corrected_value: z.unknown().optional(),
    accepted_value: z.unknown().optional(),
    reason: z.string().nullish()
});

/**
 * CorrectionOut
 */
export const zCorrectionOut = z.object({
    id: z.number().int(),
    field_path: z.string(),
    field_key: z.string(),
    kind: z.enum([
        'value',
        'exclude',
        'equivalent'
    ]),
    correction_revision: z.number().int(),
    baseline_value: z.unknown().optional(),
    previous_expected_value: z.unknown().optional(),
    corrected_value: z.unknown().optional(),
    accepted_value: z.unknown().optional(),
    reviewer: z.string(),
    reason: z.string().nullish(),
    created_at: z.string().datetime({ offset: true }),
    superseded_by: z.number().int().nullish(),
    revoked_revision: z.number().int().nullish()
});

/**
 * DiscrepancyReviewOut
 */
export const zDiscrepancyReviewOut = z.object({
    verdict: z.enum([
        'rumi_correct',
        'both_wrong',
        'analyst_correct',
        'equivalent',
        'not_determinable',
        'valid_call_out',
        'should_have_known'
    ]),
    reviewer: z.string(),
    reason: z.string().nullish(),
    reviewed_at: z.string().datetime({ offset: true })
});

/**
 * DocumentOut
 */
export const zDocumentOut = z.object({
    id: z.number().int(),
    kind: z.enum(['broker', 'proposal']),
    file_name: z.string(),
    sharepoint_path: z.string(),
    sharepoint_modified_at: z.string().datetime({ offset: true }).nullish(),
    sha256: z.string()
});

/**
 * EvidenceOut
 *
 * RUMI's evidence for a field of the paired plan (evidence.py): a
 * citation it wrote through ai-dox, with the document's wording, the value,
 * the page, and the document; or a btx field comment, its reason for the
 * value, with the page and the case document when the text names them.
 */
export const zEvidenceOut = z.object({
    source: z.enum(['citation', 'comment']),
    field_path: z.string(),
    comment: z.string(),
    needs_review: z.boolean(),
    page_number: z.number().int().nullish(),
    document_id: z.number().int().nullish(),
    document_name: z.string().nullish(),
    value: z.string().nullish()
});

/**
 * AuditFieldOut
 */
export const zAuditFieldOut = z.object({
    id: z.number().int(),
    case_id: z.number().int(),
    run_id: z.number().int(),
    field_path: z.string(),
    field_key: z.string(),
    review_id: z.number().int().nullish(),
    baseline_value: z.unknown().optional(),
    rumi_value: z.unknown().optional(),
    expected_value: z.unknown().optional(),
    evidence: z.array(zEvidenceOut).optional().default([])
});

/**
 * FieldResultOut
 */
export const zFieldResultOut = z.object({
    id: z.number().int(),
    score_id: z.number().int(),
    field_path: z.string(),
    field_key: z.string(),
    comparator: z.string().nullish(),
    outcome: z.enum([
        'correct',
        'wrong',
        'missing',
        'unexpected',
        'excluded',
        'pending_judge'
    ]),
    baseline_value: z.unknown().optional(),
    expected_value: z.unknown().optional(),
    rumi_value: z.unknown().optional(),
    ambiguous: z.boolean(),
    judge_verdict: z.string().nullish(),
    judge_differences: z.array(z.string()).nullish(),
    missing_entity: z.string().nullish()
});

/**
 * LabelUpdate
 */
export const zLabelUpdate = z.object({
    label: z.string().max(1024).nullish()
});

/**
 * ReleaseCaseOut
 */
export const zReleaseCaseOut = z.object({
    case_id: z.number().int(),
    correction_revision: z.number().int()
});

/**
 * ReleaseCreate
 */
export const zReleaseCreate = z.object({
    name: z.string().max(255).nullish(),
    allow_draft_cases: z.boolean().optional().default(false)
});

/**
 * ReleaseOut
 */
export const zReleaseOut = z.object({
    id: z.number().int(),
    name: z.string(),
    test_group: z.string(),
    cases: z.array(zReleaseCaseOut),
    skipped_case_ids: z.array(z.number().int()).optional().default([])
});

/**
 * ReviewCreate
 */
export const zReviewCreate = z.object({
    field_result_ids: z.array(z.number().int()).min(1),
    verdict: z.enum([
        'rumi_correct',
        'both_wrong',
        'analyst_correct',
        'equivalent',
        'not_determinable',
        'valid_call_out',
        'should_have_known'
    ]),
    reason: z.string().nullish(),
    corrected_values: z.record(z.unknown()).nullish()
});

/**
 * ReviewOut
 */
export const zReviewOut = z.object({
    id: z.number().int(),
    field_result_id: z.number().int().nullish(),
    audit_field_id: z.number().int().nullish(),
    verdict: z.string(),
    reviewer: z.string(),
    reason: z.string().nullish(),
    reviewed_at: z.string().datetime({ offset: true })
});

/**
 * ReviewQueueItem
 */
export const zReviewQueueItem = z.object({
    case_id: z.number().int(),
    run_id: z.number().int(),
    field_result: zFieldResultOut,
    evidence: z.array(zEvidenceOut).optional().default([]),
    source_rfp_id: z.string().nullish(),
    replay_rfp_id: z.string().nullish(),
    source_rfp_url: z.string().nullable(),
    replay_rfp_url: z.string().nullable()
});

/**
 * ReviewQueueGroup
 */
export const zReviewQueueGroup = z.object({
    field_key: z.string(),
    items: z.array(zReviewQueueItem)
});

/**
 * ReviewRevise
 *
 * A verdict on one discrepancy, new or replacing the one it has.
 */
export const zReviewRevise = z.object({
    verdict: z.enum([
        'rumi_correct',
        'both_wrong',
        'analyst_correct',
        'equivalent',
        'not_determinable',
        'valid_call_out',
        'should_have_known'
    ]),
    reason: z.string().nullish(),
    corrected_value: z.unknown().optional()
});

/**
 * RunTokensOut
 *
 * RUMI's tokens over a run's model turns.
 */
export const zRunTokensOut = z.object({
    input: z.number().int(),
    output: z.number().int(),
    cache_read: z.number().int(),
    cache_write: z.number().int()
});

/**
 * RumiVersionOut
 *
 * The version a run recorded. Versions from before runs recorded theirs
 * have the image names they were asked for by, and no runner release.
 */
export const zRumiVersionOut = z.object({
    runner_version: z.string().nullish(),
    model: z.string(),
    runner_image: z.string().nullish(),
    mcp_image: z.string().nullish(),
    model_version: z.string().nullish()
});

/**
 * RunDiscrepancyOut
 */
export const zRunDiscrepancyOut = z.object({
    field_result: zFieldResultOut,
    coverage: z.string().nullish(),
    category: z.enum([
        'plan_design',
        'rate',
        'other'
    ]),
    review: zDiscrepancyReviewOut.nullish(),
    evidence: z.array(zEvidenceOut).optional().default([])
});

/**
 * RunMatchedFieldOut
 *
 * A field RUMI got right in the score, with its coverage and category.
 */
export const zRunMatchedFieldOut = z.object({
    field_result: zFieldResultOut,
    coverage: z.string().nullish(),
    category: z.enum([
        'plan_design',
        'rate',
        'other'
    ])
});

/**
 * ScoreBucketOut
 *
 * A score's totals for one coverage and one category (scoring.breakdown).
 * coverage is None for fields outside the coverages; category is
 * plan_design (a benefit plan's fields but its rate schedule), rate (the
 * rate schedule), or other.
 */
export const zScoreBucketOut = z.object({
    coverage: z.string().nullish(),
    category: z.enum([
        'plan_design',
        'rate',
        'other'
    ]),
    scored: z.number().int(),
    correct: z.number().int(),
    discrepancies: z.number().int(),
    ambiguous: z.number().int(),
    pending_judge: z.number().int(),
    filled: z.number().int().optional().default(0),
    filled_correct: z.number().int().optional().default(0),
    both_filled: z.number().int().optional().default(0),
    both_filled_correct: z.number().int().optional().default(0),
    blank: z.number().int().optional().default(0),
    blank_expected: z.number().int().optional().default(0),
    disagreed: z.number().int().optional().default(0)
});

/**
 * RunScoreOut
 *
 * The run's latest score and its totals (scoring.summarize).
 */
export const zRunScoreOut = z.object({
    id: z.number().int(),
    correction_revision: z.number().int(),
    review_state: z.string(),
    scored_at: z.string().datetime({ offset: true }),
    scored: z.number().int(),
    correct: z.number().int(),
    discrepancies: z.number().int(),
    ambiguous: z.number().int(),
    pending_judge: z.number().int(),
    field_accuracy: z.number().nullish(),
    passed: z.boolean(),
    breakdown: z.array(zScoreBucketOut).optional().default([])
});

/**
 * LatestRunOut
 */
export const zLatestRunOut = z.object({
    run_id: z.number().int(),
    model: z.string().nullable(),
    tokens: zRunTokensOut.nullable(),
    score: zRunScoreOut
});

/**
 * CaseListItemOut
 */
export const zCaseListItemOut = zCaseOut.and(z.object({
    latest_run: zLatestRunOut.nullable()
}));

/**
 * RunDetailOut
 */
export const zRunDetailOut = z.object({
    run: zCaseRunOut,
    source_rfp_id: z.string().nullish(),
    rumi_version: zRumiVersionOut.nullish(),
    score: zRunScoreOut.nullish(),
    discrepancies: z.array(zRunDiscrepancyOut).optional().default([]),
    matched: z.array(zRunMatchedFieldOut).optional().default([]),
    new_coverages: z.array(z.string()).optional().default([]),
    source_rfp_url: z.string().nullable()
});

/**
 * SuiteCreate
 */
export const zSuiteCreate = z.object({
    test_group: z.string().nullish(),
    release: z.string().nullish(),
    replay_kind: z.string().optional().default('submission'),
    repetitions: z.number().int().gte(1).lte(10).optional().default(3),
    trigger: z.string().optional().default('manual'),
    allow_draft_cases: z.boolean().optional().default(false),
    git_sha: z.string().nullish(),
    pr_number: z.number().int().nullish()
});

/**
 * SuiteOut
 */
export const zSuiteOut = z.object({
    id: z.number().int(),
    release: z.string(),
    test_group: z.string().nullish(),
    replay_kind: z.string(),
    repetitions: z.number().int(),
    trigger: z.string(),
    state: z.string(),
    error_message: z.string().nullish(),
    estimated_runs: z.number().int().nullish(),
    skipped_case_ids: z.array(z.number().int()).optional().default([]),
    run_states: z.record(z.number().int()).optional().default({})
});

/**
 * TestGroupCreate
 */
export const zTestGroupCreate = z.object({
    name: z.string().min(1).max(255).regex(/^[a-z0-9][a-z0-9._-]*$/),
    description: z.string().nullish()
});

/**
 * TestGroupOut
 */
export const zTestGroupOut = z.object({
    id: z.number().int(),
    name: z.string(),
    description: z.string().nullish(),
    created_by: z.string(),
    case_ids: z.array(z.number().int()).optional().default([])
});

/**
 * ImportStatus
 */
export const zImportStatus = z.object({
    running: z.boolean()
});

/**
 * ImportStarted
 */
export const zImportStarted = z.object({
    workflow_id: z.string(),
    already_running: z.boolean()
});

/**
 * RunStepPlanOut
 *
 * The btx benefit plan a step changed. Only the id is sent when golden could not read the replay's plans.
 */
export const zRunStepPlanOut = z.object({
    id: z.string(),
    identifier: z.string().optional(),
    plan_type: z.string().optional(),
    coverage_type: z.string().optional()
});

/**
 * RunStepDetailOut
 *
 * What a step was about beyond its target. Each tool sends the fields that apply to it.
 */
export const zRunStepDetailOut = z.object({
    knowledge_page_title: z.string().optional(),
    search_query: z.string().optional(),
    document_name: z.string().optional(),
    pages: z.string().optional(),
    total_pages: z.number().int().optional(),
    benefit_plan: zRunStepPlanOut.optional(),
    rate_category: z.string().optional(),
    rate_designs: z.array(z.string()).optional(),
    requested_tools: z.array(z.string()).optional(),
    stop_reason: z.string().optional(),
    params: z.record(z.unknown()).optional()
});

/**
 * RunStepTargetOut
 */
export const zRunStepTargetOut = z.object({
    type: z.string(),
    coverage_id: z.string().nullable(),
    document_id: z.string().nullable()
});

/**
 * RunStepUsageOut
 *
 * A model turn's tokens.
 */
export const zRunStepUsageOut = z.object({
    input: z.number().int(),
    output: z.number().int(),
    cache_read: z.number().int(),
    cache_write: z.number().int()
});

/**
 * RunStepOut
 *
 * One step of a run's RUMI run, as a stream's step event carries it.
 */
export const zRunStepOut = z.object({
    seq: z.number().int(),
    kind: z.enum(['tool_call', 'model_turn']),
    name: z.string(),
    target: zRunStepTargetOut.nullable(),
    failed: z.boolean(),
    error_code: z.string(),
    duration_ms: z.number().int(),
    started_at: z.string().datetime({ offset: true }),
    usage: zRunStepUsageOut.nullable(),
    detail: zRunStepDetailOut.nullish()
});

/**
 * RunStarted
 */
export const zRunStarted = z.object({
    run_id: z.number().int(),
    workflow_id: z.string()
});

/**
 * SuiteReport
 */
export const zSuiteReport = z.object({
    suite_id: z.number().int(),
    state: z.string(),
    reference: z.object({
        field_accuracy: z.number().nullable(),
        case_pass_rate: z.number().nullable(),
        scored_fields: z.number().int(),
        runs: z.number().int()
    }),
    candidate: z.object({
        field_accuracy: z.number().nullable(),
        case_pass_rate: z.number().nullable(),
        scored_fields: z.number().int(),
        runs: z.number().int()
    }),
    field_accuracy_change: z.number().nullable(),
    case_pass_rate_change: z.number().nullable(),
    fields: z.record(z.record(z.array(z.number().int()))),
    ambiguous: z.record(z.array(z.number().int())),
    pairing_regressions: z.record(z.array(z.tuple([z.number().int(), z.string()]))),
    pairing_improvements: z.record(z.array(z.tuple([z.number().int(), z.string()]))),
    outdated_pattern_cases: z.array(z.number().int()),
    provisional_case_ids: z.array(z.number().int()),
    failed_runs: z.number().int(),
    unfinished_runs: z.number().int(),
    mixed_input_case_ids: z.array(z.number().int()),
    mixed_version_case_ids: z.array(z.number().int()),
    candidate_versions: z.record(z.string().nullable()),
    reference_versions: z.record(z.string().nullable()),
    unreferenced_case_ids: z.array(z.number().int()),
    error: z.string().nullable(),
    over_limit: z.object({
        regressed_fields: z.array(z.string()),
        pairing_regressions: z.array(z.string())
    }).optional(),
    passed: z.boolean().optional()
});

/**
 * CaseIntake
 */
export const zCaseIntake = z.object({
    case_id: z.number().int(),
    run_id: z.number().int(),
    data: z.record(z.unknown())
});

/**
 * ErrorResponse
 */
export const zErrorResponse = z.object({
    errorMessage: z.string(),
    appError: z.object({
        errorId: z.string(),
        timestamp: z.string().datetime({ offset: true }),
        statusCode: z.number().int(),
        message: z.string()
    })
});

/**
 * CaseOut
 */
export const zCaseOutWritable = z.object({
    id: z.number().int(),
    source_rfp_id: z.string(),
    source_folder: z.string().nullish(),
    use_final_documents: z.boolean(),
    state: z.enum([
        'awaiting_refresh',
        'draft',
        'in_review',
        'golden'
    ]),
    correction_revision: z.number().int(),
    qualifying_score_id: z.number().int().nullish(),
    label: z.string().nullish(),
    label_updated_by: z.string().nullish(),
    label_updated_at: z.string().datetime({ offset: true }).nullish(),
    created_by: z.string(),
    created_at: z.string().datetime({ offset: true })
});

/**
 * CaseListItemOut
 */
export const zCaseListItemOutWritable = zCaseOutWritable.and(z.object({
    latest_run: zLatestRunOut.nullable()
}));

/**
 * CaseRunOut
 *
 * A run on a case, with the replay RFP RUMI filled in.
 */
export const zCaseRunOutWritable = z.object({
    id: z.number().int(),
    suite_id: z.number().int().nullish(),
    replay_kind: z.string(),
    replay_rfp_id: z.string().nullish(),
    state: z.enum([
        'queued',
        'running',
        'finished',
        'failed'
    ]),
    error_message: z.string().nullish(),
    created_at: z.string().datetime({ offset: true }),
    finished_at: z.string().datetime({ offset: true }).nullish()
});

/**
 * ReviewQueueItem
 */
export const zReviewQueueItemWritable = z.object({
    case_id: z.number().int(),
    run_id: z.number().int(),
    field_result: zFieldResultOut,
    evidence: z.array(zEvidenceOut).optional().default([]),
    source_rfp_id: z.string().nullish(),
    replay_rfp_id: z.string().nullish()
});

/**
 * ReviewQueueGroup
 */
export const zReviewQueueGroupWritable = z.object({
    field_key: z.string(),
    items: z.array(zReviewQueueItemWritable)
});

/**
 * RunDetailOut
 */
export const zRunDetailOutWritable = z.object({
    run: zCaseRunOutWritable,
    source_rfp_id: z.string().nullish(),
    rumi_version: zRumiVersionOut.nullish(),
    score: zRunScoreOut.nullish(),
    discrepancies: z.array(zRunDiscrepancyOut).optional().default([]),
    matched: z.array(zRunMatchedFieldOut).optional().default([]),
    new_coverages: z.array(z.string()).optional().default([])
});

export const zListCasesQuery = z.object({
    search: z.string().nullish(),
    state: z.enum([
        'awaiting_refresh',
        'draft',
        'in_review',
        'golden'
    ]).nullish()
});

/**
 * Successful Response
 */
export const zListCasesResponse = z.array(zCaseListItemOut);

export const zCreateCaseBody = zCaseCreate;

/**
 * Successful Response
 */
export const zCreateCaseResponse = zCaseOut;

export const zDeleteCasePath = z.object({
    case_id: z.number().int()
});

/**
 * Successful Response
 */
export const zDeleteCaseResponse = z.void();

export const zGetCasePath = z.object({
    case_id: z.number().int()
});

/**
 * Successful Response
 */
export const zGetCaseResponse = zCaseOut;

export const zListCaseRunsPath = z.object({
    case_id: z.number().int()
});

/**
 * Successful Response
 */
export const zListCaseRunsResponse = z.array(zCaseRunOut);

export const zStartCaseRunBody = zCaseRunCreate;

export const zStartCaseRunPath = z.object({
    case_id: z.number().int()
});

/**
 * The run, queued, and its workflow.
 */
export const zStartCaseRunResponse = zRunStarted;

export const zDeleteCaseRunPath = z.object({
    case_id: z.number().int(),
    run_id: z.number().int()
});

/**
 * Successful Response
 */
export const zDeleteCaseRunResponse = z.void();

export const zGetCaseRunPath = z.object({
    case_id: z.number().int(),
    run_id: z.number().int()
});

/**
 * Successful Response
 */
export const zGetCaseRunResponse = zRunDetailOut;

export const zStreamCaseRunHeaders = z.object({
    'Last-Event-ID': z.number().int().optional()
});

export const zStreamCaseRunPath = z.object({
    case_id: z.number().int(),
    run_id: z.number().int()
});

/**
 * A text/event-stream of run, step and end events. A step event's
 * data is a RunStepOut.
 */
export const zStreamCaseRunResponse = z.string();

export const zUpdateLabelBody = zLabelUpdate;

export const zUpdateLabelPath = z.object({
    case_id: z.number().int()
});

/**
 * Successful Response
 */
export const zUpdateLabelResponse = zCaseOut;

/**
 * Successful Response
 */
export const zRetryAwaitingCasesResponse = z.array(zCaseOut);

export const zGetIntakePath = z.object({
    case_id: z.number().int()
});

/**
 * Successful Response
 */
export const zGetIntakeResponse = zCaseIntake;

export const zGetImportStatusPath = z.object({
    case_id: z.number().int()
});

/**
 * Successful Response
 */
export const zGetImportStatusResponse = zImportStatus;

export const zListDocumentsPath = z.object({
    case_id: z.number().int()
});

/**
 * Successful Response
 */
export const zListDocumentsResponse = z.array(zDocumentOut);

export const zGetDocumentContentPath = z.object({
    case_id: z.number().int(),
    document_id: z.number().int()
});

/**
 * The document's bytes, as an attachment named after the file.
 */
export const zGetDocumentContentResponse = z.instanceof(Blob);

export const zListCorrectionsPath = z.object({
    case_id: z.number().int()
});

/**
 * Successful Response
 */
export const zListCorrectionsResponse = z.array(zCorrectionOut);

export const zAddCorrectionBody = zCorrectionCreate;

export const zAddCorrectionPath = z.object({
    case_id: z.number().int()
});

/**
 * Successful Response
 */
export const zAddCorrectionResponse = zCorrectionOut;

export const zGetReviewQueueQuery = z.object({
    field_key: z.string().nullish(),
    case_id: z.number().int().nullish()
});

/**
 * Successful Response
 */
export const zGetReviewQueueResponse = z.array(zReviewQueueGroup);

export const zCreateReviewBody = zReviewCreate;

/**
 * Successful Response
 */
export const zCreateReviewResponse = z.array(zReviewOut);

export const zReviseReviewBody = zReviewRevise;

export const zReviseReviewPath = z.object({
    field_result_id: z.number().int()
});

/**
 * Successful Response
 */
export const zReviseReviewResponse = zReviewOut;

export const zListAuditFieldsPath = z.object({
    case_id: z.number().int()
});

/**
 * Successful Response
 */
export const zListAuditFieldsResponse = z.array(zAuditFieldOut);

export const zReviewAuditFieldBody = zAuditReviewCreate;

export const zReviewAuditFieldPath = z.object({
    audit_field_id: z.number().int()
});

/**
 * Successful Response
 */
export const zReviewAuditFieldResponse = zReviewOut;

/**
 * Successful Response
 */
export const zListTestGroupsResponse = z.array(zTestGroupOut);

export const zCreateTestGroupBody = zTestGroupCreate;

/**
 * Successful Response
 */
export const zCreateTestGroupResponse = zTestGroupOut;

export const zGetTestGroupPath = z.object({
    name: z.string()
});

/**
 * Successful Response
 */
export const zGetTestGroupResponse = zTestGroupOut;

export const zRemoveCaseFromGroupPath = z.object({
    name: z.string(),
    case_id: z.number().int()
});

/**
 * Successful Response
 */
export const zRemoveCaseFromGroupResponse = zTestGroupOut;

export const zAddCaseToGroupPath = z.object({
    name: z.string(),
    case_id: z.number().int()
});

/**
 * Successful Response
 */
export const zAddCaseToGroupResponse = zTestGroupOut;

export const zCreateReleaseBody = zReleaseCreate;

export const zCreateReleasePath = z.object({
    name: z.string()
});

/**
 * Successful Response
 */
export const zCreateReleaseResponse = zReleaseOut;

export const zGetReleasePath = z.object({
    name: z.string()
});

/**
 * Successful Response
 */
export const zGetReleaseResponse = zReleaseOut;

export const zImportDocumentsPath = z.object({
    case_id: z.number().int()
});

/**
 * Successful Response
 */
export const zImportDocumentsResponse = zImportStarted;

export const zCreateSuiteBody = zSuiteCreate;

/**
 * Successful Response
 */
export const zCreateSuiteResponse = zSuiteOut;

export const zGetSuitePath = z.object({
    suite_id: z.number().int()
});

/**
 * Successful Response
 */
export const zGetSuiteResponse = zSuiteOut;

export const zStartSuitePath = z.object({
    suite_id: z.number().int()
});

/**
 * Successful Response
 */
export const zStartSuiteResponse = zSuiteOut;

export const zGetSuiteReportPath = z.object({
    suite_id: z.number().int()
});

export const zGetSuiteReportQuery = z.object({
    limit: z.number().int().gte(0).nullish()
});

/**
 * Successful Response
 */
export const zGetSuiteReportResponse = zSuiteReport;
